import { useEffect, useRef, useState } from "react";
import { X, Flashlight, Check, Loader2, AlertCircle } from "lucide-react";
import { hapticSuccess } from "@/lib/haptics";
import { useI18n } from "@/lib/i18n.jsx";
import { beep, lookupBarcode } from "@/components/barcode/scanUtils";

const FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "itf"];
const SAME_CODE_COOLDOWN_MS = 1200;
const SCAN_EVERY_MS = 90;

// Native BarcodeDetector where it supports retail formats (Chrome/Android), else the zxing-cpp WASM ponyfill (iOS).
async function makeDetector() {
  try {
    if ("BarcodeDetector" in window) {
      const sup = await window.BarcodeDetector.getSupportedFormats();
      if (sup.includes("ean_13")) return new window.BarcodeDetector({ formats: FORMATS.filter((f) => sup.includes(f)) });
    }
  } catch {}
  const { BarcodeDetector, setZXingModuleOverrides } = await import("barcode-detector/ponyfill");
  // Self-hosted WASM (no CDN) so it works behind our CSP and on flaky networks.
  setZXingModuleOverrides({ locateFile: (path, prefix) => (path.endsWith(".wasm") ? "/zxing/zxing_reader-3.1.6.wasm" : prefix + path) });
  const d = new BarcodeDetector({ formats: FORMATS });
  await d.detect(new ImageData(8, 8));
  return d;
}

async function openCamera() {
  const base = { width: { ideal: 1920 }, height: { ideal: 1080 } };
  const get = (video) => navigator.mediaDevices.getUserMedia({ video, audio: false });
  let stream = await get({ ...base, facingMode: { ideal: "environment" } });
  // Android often hands us the ultra-wide / fixed-focus lens. Labels are readable after permission:
  // the main rear camera is the lowest-numbered "camera2 N, facing back".
  try {
    const cams = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "videoinput");
    const back = cams.filter((d) => /back|rear|environment|خلف/i.test(d.label));
    const num = (d) => { const m = d.label.match(/camera2?\s*(\d+)/i); return m ? Number(m[1]) : 99; };
    const main = back.sort((a, b) => num(a) - num(b))[0];
    const current = stream.getVideoTracks()[0]?.getSettings?.().deviceId;
    if (main && main.deviceId && main.deviceId !== current && /android/i.test(navigator.userAgent)) {
      stream.getTracks().forEach((t) => t.stop());
      stream = await get({ ...base, deviceId: { exact: main.deviceId } });
    }
  } catch {}
  await tuneTrack(stream.getVideoTracks()[0]);
  return stream;
}

async function tuneTrack(track) {
  let caps = {};
  try { caps = track.getCapabilities?.() || {}; } catch {}
  const adv = [];
  if (caps.focusMode?.includes("continuous")) adv.push({ focusMode: "continuous" });
  // A little zoom lets users hold the phone farther away (inside the focus range) while the barcode stays big.
  if (caps.zoom && /android/i.test(navigator.userAgent)) adv.push({ zoom: Math.min(caps.zoom.max, Math.max(caps.zoom.min, 2)) });
  for (const c of adv) { try { await track.applyConstraints({ advanced: [c] }); } catch {} }
}

async function refocus(track) {
  try {
    const caps = track.getCapabilities?.() || {};
    if (caps.focusMode?.includes("single-shot")) {
      await track.applyConstraints({ advanced: [{ focusMode: "single-shot" }] });
      if (caps.focusMode.includes("continuous")) setTimeout(() => track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {}), 1200);
    }
  } catch {}
}

function ManualEntry({ en, onSubmit }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const code = val.replace(/\D/g, "");
  const ok = /^\d{8,14}$/.test(code);
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="block mx-auto text-xs text-white/85 underline underline-offset-4 py-1" data-testid="barcode-manual-toggle">
        {en ? "Enter barcode manually" : "أدخل الباركود يدوياً"}
      </button>
    );
  }
  const submit = () => { if (ok) { onSubmit(code); setVal(""); setOpen(false); } };
  return (
    <div className="flex items-center gap-2 bg-white rounded-full p-1.5 ps-4 shadow-lg" data-testid="barcode-manual-form">
      <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()}
        inputMode="numeric" dir="ltr" placeholder={en ? "e.g. 6281062358658" : "مثال: 6281062358658"}
        className="flex-1 min-w-0 bg-transparent text-neutral-900 text-sm font-semibold tracking-wider focus:outline-none" data-testid="barcode-manual-input" />
      <button onClick={submit} disabled={!ok} className="orange-gradient text-white text-sm font-bold rounded-full px-4 py-2 disabled:opacity-50" data-testid="barcode-manual-submit">
        {en ? "Add" : "أضف"}
      </button>
      <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full text-neutral-500 flex items-center justify-center" data-testid="barcode-manual-cancel"><X className="w-4 h-4" /></button>
    </div>
  );
}

export default function LiveWebScanner({ onDone, onClose, onUnavailable }) {
  const { lang } = useI18n();
  const en = lang === "en";
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const last = useRef({ code: "", at: 0 });
  const resultsRef = useRef([]);
  const [results, setResults] = useState([]);
  const [pending, setPending] = useState(0);
  const [ready, setReady] = useState(false);
  const [torch, setTorch] = useState(null);
  const handleRef = useRef(null);

  useEffect(() => {
    let alive = true, timer = null;
    const stop = () => { clearTimeout(timer); streamRef.current?.getTracks().forEach((t) => t.stop()); };
    const handle = async (code, manual = false) => {
      const now = Date.now();
      if (!/^\d{8,14}$/.test(code) || (!manual && (code === last.current.code || now - last.current.at < SAME_CODE_COOLDOWN_MS))) return;
      last.current = { code, at: now };
      beep(); hapticSuccess();
      if (navigator.vibrate) navigator.vibrate(60);
      setPending((n) => n + 1);
      const r = await lookupBarcode(code);
      resultsRef.current = [...resultsRef.current, r];
      if (alive) { setResults(resultsRef.current); setPending((n) => n - 1); }
    };
    handleRef.current = handle;
    (async () => {
      try {
        const [stream, detector] = await Promise.all([openCamera(), makeDetector()]);
        if (!alive) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        v.srcObject = stream;
        await v.play();
        setReady(true);
        const track = stream.getVideoTracks()[0];
        try { if (track.getCapabilities?.().torch) setTorch(false); } catch {}
        const tick = async () => {
          if (!alive) return;
          if (v.readyState >= 2) {
            try {
              const found = await detector.detect(v);
              const hit = found.find((b) => /^\d{8,14}$/.test(String(b.rawValue).replace(/\D/g, "")));
              if (hit) await handle(String(hit.rawValue).replace(/\D/g, ""));
            } catch {}
          }
          timer = setTimeout(tick, SCAN_EVERY_MS);
        };
        tick();
      } catch {
        if (alive) onUnavailable?.();
      }
    })();
    return () => { alive = false; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    if (resultsRef.current.length) onDone?.(resultsRef.current); else onClose?.();
  };

  const toggleTorch = async () => {
    try {
      await streamRef.current.getVideoTracks()[0].applyConstraints({ advanced: [{ torch: !torch }] });
      setTorch(!torch);
    } catch {}
  };

  const lastR = results[results.length - 1];
  const lastName = lastR?.food ? (en ? lastR.food.name_en || lastR.food.name_ar : lastR.food.name_ar) : null;

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col text-white" dir={en ? "ltr" : "rtl"} data-testid="barcode-scanner">
      <video ref={videoRef} playsInline muted autoPlay onClick={() => streamRef.current && refocus(streamRef.current.getVideoTracks()[0])}
        className="absolute inset-0 w-full h-full object-cover" data-testid="barcode-live-region" />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="relative w-[85%] max-w-[380px] aspect-[2/1] rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]">
          {["top-0 left-0 border-t-4 border-l-4 rounded-tl-2xl", "top-0 right-0 border-t-4 border-r-4 rounded-tr-2xl", "bottom-0 left-0 border-b-4 border-l-4 rounded-bl-2xl", "bottom-0 right-0 border-b-4 border-r-4 rounded-br-2xl"].map((c) => (
            <span key={c} className={`absolute w-8 h-8 border-white ${c}`} />
          ))}
          {ready && <span className="absolute left-3 right-3 h-0.5 bg-orange-500 shadow-[0_0_12px_rgba(249,115,22,0.9)] animate-[scanline_1.8s_ease-in-out_infinite]" />}
        </div>
      </div>
      <style>{"@keyframes scanline{0%,100%{top:12%}50%{top:86%}}"}</style>
      <header className="relative z-10 p-4 flex items-center justify-between" style={{ paddingTop: "max(1rem, env(safe-area-inset-top))" }}>
        <button onClick={finish} className="w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center" data-testid="barcode-close-btn" aria-label={en ? "Close" : "إغلاق"}>
          <X className="w-5 h-5" />
        </button>
        <div className="text-sm font-semibold">{en ? "Scan barcode" : "مسح باركود"}</div>
        {torch === null ? <div className="w-10" /> : (
          <button onClick={toggleTorch} className={`w-10 h-10 rounded-full backdrop-blur flex items-center justify-center ${torch ? "bg-white text-black" : "bg-black/50"}`} data-testid="barcode-torch-btn">
            <Flashlight className="w-5 h-5" />
          </button>
        )}
      </header>
      <div className="relative z-10 mt-auto p-5 space-y-3" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
        {lastR && (
          <div key={results.length} className="flex items-center gap-2 bg-white text-neutral-900 rounded-2xl px-3 py-2.5 shadow-lg animate-in fade-in slide-in-from-bottom-2" data-testid="barcode-last-result">
            {lastR.status === "ok" ? <Check className="w-4 h-4 text-emerald-600 shrink-0" /> : <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />}
            <div className="text-sm font-semibold truncate flex-1">
              {lastName || (en ? "Not in our database — you'll snap its label after" : "غير موجود في قاعدة البيانات — بتصوّر ملصقه بعد الانتهاء")}
            </div>
          </div>
        )}
        <div className="text-center text-sm text-white/90 bg-black/40 backdrop-blur rounded-full px-4 py-2 mx-auto w-fit" data-testid="barcode-live-hint">
          {!ready ? (en ? "Opening camera…" : "جارٍ فتح الكاميرا…")
            : results.length ? (en ? "Scan the next product, or tap Done" : "امسح المنتج التالي أو اضغط تم")
            : (en ? "Hold the phone 15–20 cm away — it scans automatically" : "أبعد الجوال 15–20 سم عن الباركود — يُقرأ تلقائياً")}
          {ready && !results.length && /android/i.test(navigator.userAgent) && (
            <div className="text-[11px] text-white/70 mt-0.5">{en ? "Blurry? Tap the screen to focus" : "الصورة غير واضحة؟ اضغط على الشاشة للتركيز"}</div>
          )}
        </div>
        {ready && <ManualEntry en={en} onSubmit={(code) => handleRef.current?.(code, true)} />}
        {(results.length > 0 || pending > 0) && (
          <button onClick={finish} disabled={pending > 0} className="w-full orange-gradient text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-70" data-testid="barcode-done-btn">
            {pending > 0 ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {en ? `Done · ${results.length} product${results.length === 1 ? "" : "s"}` : `تم (${results.length} منتج)`}
          </button>
        )}
      </div>
    </div>
  );
}
